<template>
    <div class="patient-component">
        <card title="Patient Record">
            <template #header-right>
                <history-modal v-if="patient?.id" v-model="patient" />
            </template>

            <form>
                <NameSearchComponent v-model="displayName" :disable-fields="disableFields" :named-person="patient"
                    :search-mode="disableFields" @input="nameChanged" @person-update="patientUpdated" />

                <b-form-row>
                    <b-col>
                        <b-form-row>
                            <b-col>
                                <fg-input v-model="patient.preferredName" :disabled="disableFields"
                                    label="Preferred Name" />
                            </b-col>
                            <b-col>
                                <fg-input v-model="patient.pronouns" :disabled="disableFields" label="Pronouns" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <fg-input id="patientEmail" v-model="patient.emailAddress" :disabled="disableFields"
                                    label="Email" type="email" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <small v-if="patient.height" class="text-muted pull-right" style="margin-top: 2px;">
                                    {{ Math.floor(patient.height / 12) }} feet {{ patient.height % 12 }} in
                                </small>
                                <fg-input v-model="patient.height" :disabled="disableFields" label="Height (in)"
                                    type="number" min="0" step="1"
                                    @keydown="allowOnlyDigits"
                                    @blur="patient.height = !patient.height ? patient.height : Math.max(Math.floor(patient.height), 0)" />
                            </b-col>
                            <b-col>
                                <fg-input v-model="patient.weight" :disabled="disableFields" label="Weight (lb)"
                                    type="number" min="0" step="1"
                                    @keydown="allowOnlyDigits"
                                    @blur="patient.weight = !patient.weight ? patient.weight : Math.max(Math.floor(patient.weight), 0)" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="3">
                                <fg-input v-model="patient.licenseState" :disabled="disableFields" label="License State"
                                    maxlength="3"></fg-input>
                            </b-col>
                            <b-col cols="5">
                                <fg-input v-model="patient.licenseNumber" :disabled="disableFields"
                                    label="License Number"></fg-input>
                            </b-col>
                            <b-col cols="4">
                                <fg-input label="License Expiration">
                                    <el-date-picker v-model="patient.licenseExpirationDate" :disabled="disableFields"
                                        format="MM/dd/yyyy" type="date" />
                                </fg-input>
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="4">
                                <fg-input v-model="patient.ssn" :disabled="!canSeeSSN || disableFields" label="SSN #"
                                    maxlength="11"></fg-input>
                            </b-col>
                            <b-col cols="8">
                                <label>Carriers</label>
                                <multiselect v-model="patient.carriers" :options="carrierOptions" group-label="label"
                                    group-values="carriers" :close-on-select="false" multiple group-select />
                            </b-col>
                        </b-form-row>
                    </b-col>
                    <b-col>
                        <b-row>
                            <b-col>
                                <fg-input v-model="patient.cellNumber" :disabled="disableFields" label="Cell" />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <fg-input v-model="patient.phoneNumber" :disabled="disableFields" label="Phone" />
                            </b-col>
                        </b-row>
                        <b-form-row>
                            <b-col cols="4">
                                <fg-input v-model="patient.sex" :disabled="disableFields" label="Sex (M/F)" required
                                    maxlength="1" />
                            </b-col>
                            <b-col cols="4">
                                <fg-input v-model="patient.gender" :disabled="disableFields" label="Gender (M/F)"
                                    maxlength="1" />
                            </b-col>
                            <b-col cols="4">
                                <fg-input v-model="patient.sexAssignedAtBirth" :disabled="disableFields"
                                    label="Sex Assigned at Birth (M/F)" maxlength="1" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col cols="4">
                                <fg-input label="DOB *">
                                    <el-date-picker v-model="patient.dateOfBirth" :disabled="disableFields"
                                        format="MM/dd/yyyy" type="date" />
                                </fg-input>
                            </b-col>
                            <b-col cols="8">
                                <EnumSelector v-model="patient.speciesCode" :disabled="disableFields"
                                    :enum-codes="speciesCodes" label="Species" />
                            </b-col>
                        </b-form-row>
                        <b-form-row>
                            <b-col>
                                <EnumSelector v-model="patient.residenceCode" :disabled="disableFields"
                                    :enum-codes="residenceCode" label="Residence Code">
                                </EnumSelector>
                            </b-col>
                            <b-col>
                                <EnumSelector v-model="patient.primaryLanguage" :disabled="disableFields"
                                    :enum-codes="language" label="Language"></EnumSelector>
                            </b-col>
                            <b-col cols="4">
                                <EnumSelector v-model="patient.defaultDelivery" :disabled="disableFields"
                                    :enum-codes="deliveryCodes" clearable label="Delivery"></EnumSelector>
                            </b-col>
                        </b-form-row>
                    </b-col>
                </b-form-row>
                <b-form-row>
                    <b-col offset="6" cols="6">
                        <checkbox v-model="pregnant" :disabled=disableFields inline>Pregnant
                        </checkbox>
                        <checkbox v-model="lactating" :disabled=disableFields inline>Lactating
                        </checkbox>
                        <checkbox v-model="smoker" :disabled=disableFields inline>Smoker
                        </checkbox>
                        <checkbox v-model="medSyncOptOut" :disabled=disableFields inline>MedSync Opt-Out
                        </checkbox>
                        <checkbox v-model="patient.childCaps" :disabled=disableFields inline>Use Safety Caps
                        </checkbox>
                        <checkbox v-model="patient.signatureRequired" :disabled=disableFields inline>Signature Required
                        </checkbox>
                        <checkbox v-model="deceased" :disabled=disableFields inline>Deceased
                        </checkbox>
                        <checkbox v-model="patient.isHighTouch" :disabled=disableFields inline>High Touch
                        </checkbox>
                    </b-col>
                </b-form-row>

                <hr>

                <div>
                    <h6>
                        Addresses
                        <HelpPopup icon="fa fa-info-circle" :text="addressUsageHelp" />
                    </h6>

                    <AddressFormFields v-if="patient?.addresses?.length === 1" v-model="patient.addresses[0]"
                        :disable-fields="disableFields" :is-patient-address="true" />

                    <div class="addresses-container" v-if="patient?.addresses?.length > 1">
                        <PatientAddressCard v-for="pa in patient.addresses" :key="pa.id" :address="pa"
                            @edit="editAddress(pa)" @delete="deleteAddress(pa)" />
                    </div>

                    <b-button variant="primary" size="sm" @click="addNewAddress" :disabled="disableFields">
                        <i class="fa fa-plus-circle"></i>
                        Add Additional Address
                    </b-button>
                </div>

                <b-modal :visible="!!selectedAddress" title="Patient Address" size="lg" @hide="selectedAddress = null"
                    @ok="processAddressFromModal">
                    <AddressFormFields v-model="selectedAddress" :is-patient-address="true"
                        :show-is-primary="showIsPrimaryAddress" />
                </b-modal>

                <AddressApprovalModal v-model="showAddressApprovalModal" :original-address="addressApprovalOriginal"
                    :normalized-address="addressApprovalNormalized" @approve="resolveAddressApproval('approve')"
                    @override="resolveAddressApproval('override')" />

                <hr>

                <b-row align-h="center" align-v="start">
                    <div class="col-7">
                        <fg-input label="Alert">
                            <textarea v-model="patient.alert" :disabled="disableFields" class="textarea-autosize form-control"></textarea>
                        </fg-input>
                    </div>
                    <div class="col-3">
                        <fg-input label="Date of Last HIPAA Sig">
                            <el-date-picker v-model="patient.hipaaDocsReceived" :disabled="true" format="MM/dd/yyyy"
                                type="date"></el-date-picker>
                        </fg-input>
                    </div>
                </b-row>
                <hr>

                <!--Other Medications-->
                <b-row>
                    <b-col>
                        <other-medications-component ref="otherMedsComponent" :patient="patient"
                            :disabled="disableFields || !canManageNonProfileMeds" />
                        <hr />
                    </b-col>
                </b-row>

                <!--Allergies-->
                <b-row>
                    <b-col>
                        <b-alert v-if="escriptAllergies.length > 0" :show="true" variant="info" class="mb-2">
                            <i class="fa fa-pills"></i>
                            <strong> eScript Allergies:</strong>
                            {{ escriptAllergies.map(a => a.drugText || a.adverseEventText).filter(Boolean).join(', ') }}
                        </b-alert>
                        <GSDDAllergyComponent ref="allergiesComponent" v-model="patientAllergies"
                            :disable='disableFields' :patient="patient" :update="updateAllergies">
                        </GSDDAllergyComponent>

                        <hr>
                    </b-col>
                </b-row>

                <!--Diagnosis-->
                <b-row>
                    <b-col>
                        <GSDDDiagnosisComponent ref="diagnosisComponent" v-model="patientDiagnosis"
                            :disable='disableFields || !canManageDiagnoses' :patient="patient" :update="updateDiagnosis" />
                        <hr>
                    </b-col>
                </b-row>

                <!-- Program  Selection -->
                <b-row>
                    <b-col>
                        <SearchComponent v-model="selectedProgram" :disabled="disableFields" label="Programs"
                            prepopulate :params="{ active: true }" search-u-r-l="/Program">
                        </SearchComponent>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-list-group>
                            <b-list-group-item v-for="pp in patientPrograms" :key="`${pp.programID}`">
                                <b-button-close @click="removeProgram(pp.programID)"></b-button-close>
                                <b-row>
                                    <b-col cols="3"><span>{{ pp.programName }}</span></b-col>
                                    <b-col cols="2">
                                        <fg-input v-model="pp.identifier" :disabled="disableFields"
                                            placeholder="Identifier">
                                        </fg-input>
                                    </b-col>
                                </b-row>
                            </b-list-group-item>
                        </b-list-group>
                    </b-col>
                </b-row>
                <hr>

                <!-- Eligible Programs -->
                <b-row>
                    <b-col>
                        <PatientProgramEligibilityComponent v-model="patient" :compact="true" />
                    </b-col>
                </b-row>

                <hr>

                <!-- Eligibility / Insurance Plans -->
                <b-row v-if="!hideInsurance">
                    <b-col>
                        <b-row v-if="!disableFields" class="mt-3">
                            <b-col>
                                <EligibilityComponent v-model="patient" :disabled="!isFormValid"
                                    @eligibilityCheck="eligibilityCheck" @plan-clicked="combinedPlanToAdd = $event">
                                </EligibilityComponent>
                            </b-col>
                        </b-row>
                        <hr>
                        <b-row v-if="!disableFields">
                            <b-col>
                                <PatientPlansComponent v-model="patient" :addTemplate="combinedPlanToAdd"
                                    :disabled="!isFormValid" @hide="combinedPlanToAdd = null"></PatientPlansComponent>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>

            </form>

            <b-alert v-if="eligibleProgramRequired" variant="warning" show class="mt-3">
                <i class="fas fa-exclamation-triangle"></i>
                At least one eligible program is required before saving this patient. Please add an eligible program using the section above.
            </b-alert>

            <NewClearSaveButtons :disable="disableFields" :disable-clear="disableFields" :disable-save="!isFormValid"
                :titles="_buttonTitles" @cancel-clicked="backToPatientDashboard"
                @clear-clicked="showConfirmationModal" @new-clicked="createPatient"
                @save-clicked="submitUserInformation" />
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import axios, { AxiosError } from 'axios';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
import ClinicalPermissionsMixin from '@/mixins/ClinicalPermissionsMixin';

import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Lock, LockHandler } from '@/mixins/LockHandler';
import { DeliveryCode } from '@/models/DeliveryCode';
import { Program } from '@/models/Program';
import { Language, Patient, PatientAddress, PatientAddressType, PatientProgram, ResidenceCode } from '@/models/Patient';
import EnumSelector from '@/components/EnumSelector.vue';
import NameSearchComponent from '@/components/NameSearchComponent.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import NewClearSaveButtons from '@/components/NewClearSaveButtons.vue';
import AddressFormFields from '@/components/AddressFormFields.vue';
import AddressApprovalModal from '@/components/Shipping/AddressApprovalModal.vue';
import { AddressDetail, AddressValidationResult } from '@/models/AddressValidation';
import HelpPopup from '@/components/HelpPopup.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import EligibilityComponent from "@/components/Insurance/EligibilityComponent.vue";
import { GSDDPatientAllergy, GSDDPatientDiagnosis } from "@/models/GSDD";
import { Allergy } from "@/models/Allergy";
import PatientPlansComponent from "@/components/Insurance/PatientPlansComponent.vue";
import GSDDAllergyComponent from "@/components/GSDDAllergies/GSDDAllergyComponent.vue";
import GSDDDiagnosisComponent from "@/components/GSDDDiagnosis/GSDDDiagnosisComponent.vue";

import { CombinedPlan } from "@/models/CombinedPlan";
import { SpeciesCode } from "@/models/SpeciesCode";
import OtherMedicationsComponent from "@/components/Patient/OtherMedicationsComponent.vue";
import Multiselect from "vue-multiselect";
import HistoryComponent from "@/components/HistoryComponent.vue";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import PatientAddressCard from './PatientAddressCard.vue';
import PatientProgramEligibilityComponent from './PatientProgramEligibilityComponent.vue';
import { EntityType } from '@/models/EntityType';
import NotificationTypes = NotificationOptions.NotificationTypes;
import {toRaw} from "vue";
import { ButtonTitles } from "@/components/ButtonTitles";

@Component({
    name: "PatientFormComponent",
    components: {
        HistoryModal,
        HistoryComponent,
        Multiselect,
        OtherMedicationsComponent,
        NameSearchComponent,
        SearchComponent,
        GSDDAllergyComponent,
        GSDDDiagnosisComponent,
        NewClearSaveButtons,
        AddressFormFields,
        AddressApprovalModal,
        HelpPopup,
        EnumSelector,
        Checkbox,
        [DatePicker.name]: DatePicker,
        EligibilityComponent,
        PatientPlansComponent,
        PatientAddressCard,
        PatientProgramEligibilityComponent
    },
})
export default class PatientFormComponent extends Mixins(LockHandler, DisableAutocompleteMixin, ClinicalPermissionsMixin) {
    @Prop({
        default: () => {
            return {
                new: "New Patient",
                clear: "Clear",
                save: "Save Changes",
                cancel: "",
            };
        },
    }) private buttonTitles!: ButtonTitles;
    @Prop({ type: Boolean, default: false }) private createNewPatient!: boolean;
    @Prop() private escriptResponsePatient?: Patient;
    @Prop({ default: () => [] }) private escriptDiagnoses!: GSDDPatientDiagnosis[];
    @Prop({ default: () => [] }) private escriptAllergies!: Allergy[];
    @Prop({ type: Boolean, default: false }) private hideInsurance!: boolean;
    @Ref('allergiesComponent') private allergiesComponent!: GSDDAllergyComponent;
    @Ref('otherMedsComponent') private otherMedsComponent!: OtherMedicationsComponent;
    private patient: Patient = new Patient();
    private patientPrograms: PatientProgram[] = [];
    private selectedProgram: Program = new Program();
    private patientAllergies: GSDDPatientAllergy[] = [];
    protected patientDiagnosis: GSDDPatientDiagnosis[] = [];

    private combinedPlanToAdd: CombinedPlan | null = null;
    private displayName = { firstName: "", middleName: "", lastName: "" };
    private nameIntroduced: boolean = false;
    private patientLock: Lock | null = null;
    protected deliveryCodes = DeliveryCode;
    protected language = Language;
    protected residenceCode = ResidenceCode;
    protected speciesCodes = SpeciesCode;
    private newMode = false;
    private updateAllergies = false;
    private updateDiagnosis = false;
    private allergiesModified = false;
    private isSaving: boolean = false;
    private selectedAddress: PatientAddress | null = null;
    private requireEligibleProgramForPatient: boolean = false;
    private requireAddressValidationOnSave: boolean = false;

    private showAddressApprovalModal: boolean = false;
    private addressApprovalOriginal: AddressDetail | null = null;
    private addressApprovalNormalized: AddressDetail | null = null;
    private addressApprovalResolver: ((choice: 'approve' | 'override' | 'cancel') => void) | null = null;

    protected carrierOptions = [{ label: "All Carriers", carriers: ['UPS', 'USPS', 'FedEx'] }];

    get routePatientId(): number {
        const res = parseInt(this.$route.params.id);
        return isNaN(res) ? 0 : res;
    }

    get canSeeSSN() {
        return this.$permissions.some(p => p.permissionCodeName === "Patient Dashboard/View Social Security Number");
    }

    get disableFields(): boolean {
        const lockedForUser = !this.patientLock;
        const noEntityLoaded = !(this.patient && this.patient.id);
        // Disable fields if
        // ** its locked for the user
        // ** or if there is no object loaded and
        return (lockedForUser || noEntityLoaded) && !this.newMode;
    }

    get hasEligibleProgram(): boolean {
        return this.patient?.programEligibilities?.some(pe => pe.eligible) ?? false;
    }

    get eligibleProgramRequired(): boolean {
        // Enforce on both new and existing patients when config flag is enabled
        return this.requireEligibleProgramForPatient && !this.hasEligibleProgram;
    }

    get isFormValid() {
        const baseValid = !!(this.nameIntroduced && this.patient.sex && !this.isSaving);
        if (!baseValid) return false;

        // If eligible program is required but none exist, form is invalid
        if (this.eligibleProgramRequired) {
            return false;
        }

        return true;
    }

    // pregnant
    get pregnant(): any {
        return !!(this.patient && this.patient.pregnant);
    }

    // noinspection JSUnusedGlobalSymbols
    set pregnant(value: any) {
        if (!this.patient) return;
        this.patient.pregnant = value ? new Date() : undefined;
    }

    // lactating
    get lactating(): any {
        return !!(this.patient && this.patient.lactating);
    }

    // noinspection JSUnusedGlobalSymbols
    set lactating(value: any) {
        if (!this.patient) return;
        this.patient.lactating = value ? new Date() : undefined;
    }

    // smoker
    get smoker(): any {
        return !!(this.patient && this.patient.smoker);
    }

    // noinspection JSUnusedGlobalSymbols
    set smoker(value: any) {
        if (!this.patient) return;
        this.patient.smoker = value ? new Date() : undefined;
    }

    // medSyncOptOut
    get medSyncOptOut(): any {
        return !!(this.patient && this.patient.medSyncOptOutDate);
    }

    // noinspection JSUnusedGlobalSymbols
    set medSyncOptOut(value: any) {
        if (!this.patient) return;
        this.patient.medSyncOptOutDate = value ? new Date() : undefined;
    }

    get deceased(): any {
        return !!(this.patient && this.patient.deceased);
    }

    set deceased(value: any) {
        if (!this.patient) return;
        const date = value ? new Date() : undefined;

        this.patient.deceased = date;
        this.patient.textMessageOptOutDate = date;
        this.patient.textMessageOptOutReason = value ? 'Deceased' : undefined;
    }

    protected get _buttonTitles(): ButtonTitles {
        if (!this.routePatientId) {
            return this.buttonTitles;
        }

        return {
            save: "Save Changes",
            clear: "",
            new: "",
            cancel: "Back to Dashboard",
        };
    }

    showIsPrimaryAddress() {
        return this.patient.addresses.find(pa => pa.isPrimary)?.id !== this.selectedAddress?.id;
    }

    get addressUsageHelp(): string {
        return 'The Primary address is used for orders by default. If a different address is marked as '
            + '"Shipping", the patient\'s automatic refills are sent there instead.';
    }

    created() {
        console.log("%c Created PatientComponent", "color: green");
        this.loadConfiguration();

        // If route as a PatientId as argument, load its data by default.
        if (this.routePatientId)
            this.fetchPatient({ id: this.routePatientId } as Patient);

        if (this.createNewPatient)
            this.createPatient();
    }

    async loadConfiguration() {
        try {
            const response = await axios.get<boolean>("/configuration/RequireEligibleProgramForPatient");
            this.requireEligibleProgramForPatient = response.data;
        } catch (err) {
            console.error("Error loading RequireEligibleProgramForPatient configuration", err);
        }
        try {
            const response = await axios.get<boolean>("/configuration/EnablePatientAddressValidationOnSave");
            this.requireAddressValidationOnSave = response.data;
        } catch (err) {
            console.error("Error loading EnablePatientAddressValidationOnSave configuration", err);
        }
    }

    eligibilityCheck(eligibilityCallback: Function) {
        this.submitUserInformation(eligibilityCallback);
    }

    nameChanged(val: any) {
        this.nameIntroduced = !!val.firstName && !!val.lastName;
    }

    @Watch("patient")
    onPatientChanged(value: Patient | null, oldValue: Patient | null) {
        if (!value) {
            this.displayName = {
                firstName: "",
                middleName: "",
                lastName: ""
            };
            return;
        }
        const patient: Patient = value as Patient;

        if (!patient.id) {
            patient.firstName = this.displayName.firstName;
            patient.middleName = this.displayName.middleName;
            patient.lastName = this.displayName.lastName;
        } else {
            if (!oldValue || oldValue.id != patient.id) {
                // Get the full patient object instead of the tiny stub we get from backend.
                this.fetchPatient(patient);
            }
        }

        this.displayName = {
            firstName: patient.firstName,
            middleName: patient.middleName,
            lastName: patient.lastName
        };
        this.nameIntroduced = !!patient.lastName && !!patient.firstName;
    }

    @Watch('selectedProgram')
    onSelectedProgramChanged(value: Program | null, _oldValue: Program | null) {
        if (value?.id) {
            this.addProgram(value);
        }
    }

    async obtainLockOnPatient(patient: Patient) {
        try {
            this.patientLock = await this.addLockWithURL(`/Patient/${patient.id}/lock`, 60000);
        } catch (err) {
            const error = err as AxiosError;
            console.error(`Added lock -- error == ${error}`);
            if (error.response?.status == 418) {
                // patient locked
                console.log(error.response.data);
                this.showLockedMessage(
                    EntityType.Patient,
                    patient?.id.toString(),
                    patient?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(err));
            }
        }
    }

    async fetchPatient(patient: Patient) {
        await this.obtainLockOnPatient(patient);

        const patientPromise = axios.get(`/Patient/${patient.id}`);
        const programPromise = axios.get(`/Patient/${patient.id}/programs`);
        const allergyPromise = axios.get(`/Allergy/GetGSDDAllergiesForPatient/${patient.id}`);
        const diagnosisPromise = axios.get(`/Diagnosis/Patient/${patient.id}`);

        try {
            const response = await patientPromise;
            this.patient = new Patient(response.data);
        } catch (err) {
            const error = err as AxiosError;
            console.error("Error while loading the Patient", error);
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Patient,
                    patient?.id.toString(),
                    patient?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }

        try {
            const response = await programPromise;
            this.patientPrograms = response.data;
        } catch (err) {
            const error = err as AxiosError;
            console.error("Error while loading Programs", error);
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Patient,
                    patient?.id.toString(),
                    patient?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }

        try {
            const response = await allergyPromise;
            this.patientAllergies = response.data.sort((a: GSDDPatientAllergy, b: GSDDPatientAllergy) => {
                if (!a.created && !b.created) return 0;
                if (!a.created) return 1;
                if (!b.created) return -1;
                return new Date(b.created).getTime() - new Date(a.created).getTime();
            });
        } catch (err) {
            const error = err as AxiosError;
            console.error("Error while loading the insurance allergies", error);
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Patient,
                    patient?.id.toString(),
                    patient?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }

        try {
            const response = await diagnosisPromise;
            this.patientDiagnosis = response.data.sort((a: GSDDPatientDiagnosis, b: GSDDPatientDiagnosis) => {
                if (!a.created && !b.created) return 0;
                if (!a.created) return 1;
                if (!b.created) return -1;
                return new Date(b.created).getTime() - new Date(a.created).getTime();
            });
        } catch (err) {
            const error = err as AxiosError;
            console.error("Error while loading the insurance diagnosis", error);
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Patient,
                    patient?.id.toString(),
                    patient?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }

        this.$nextTick(() => { this.allergiesModified = false; });
    }

    patientUpdated(value: any) {
        if (value instanceof Patient) {
            this.patient = value;
        } else {
            this.patient = new Patient(value);
        }

        this.nameIntroduced = !!this.patient.firstName && !!this.patient.lastName;
    }

    async showConfirmationModal() {
        try {
            const value = await this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
                title: 'Confirm',
                okVariant: 'danger',
                centered: true,
                noFade: true
            });

            if (!value) return;

            if (this.patientLock && this.patientLock.refreshURL) {
                this.releaseLockAtURL(this.patientLock.refreshURL);
                this.patientLock = null;
            }
            this.patient = new Patient();
            this.displayName = {
                firstName: "",
                middleName: "",
                lastName: ""
            };
            this.patientPrograms = [];
            this.selectedProgram = new Program();
            this.newMode = false;
            (this.allergiesComponent as any).clearComponent();
        } catch (err) {
            console.error("Error caught in showConfirmationModal()", err);
        }
    }

    createPatient() {
        this.newMode = true;
        this.displayName = {
            firstName: "",
            middleName: "",
            lastName: ""
        }
        this.patient = new Patient();

        if (this.escriptResponsePatient) {
            for (const [key, value] of Object.entries(this.escriptResponsePatient)) {
                if (value !== undefined && value !== null) {
                    this.$set(this.patient, key, value);
                }
            }
            this.displayName = {
                firstName: this.patient.firstName,
                middleName: this.patient.middleName,
                lastName: this.patient.lastName
            };
            this.nameIntroduced = !!this.patient.firstName && !!this.patient.lastName;
        }

        // Pre-populate diagnoses from eScript data for new patients only.
        // For existing patients, diagnoses are loaded from the DB by GSDDDiagnosisComponent.
        if (!this.patient.id && this.escriptDiagnoses?.length > 0) {
            this.patientDiagnosis = [...this.escriptDiagnoses];
        }

        window.scrollTo(0, 0);
    }

    @Watch("showAddressApprovalModal")
    onAddressApprovalVisibility(visible: boolean) {
        if (!visible) this.resolveAddressApproval('cancel');
    }

    private resolveAddressApproval(choice: 'approve' | 'override' | 'cancel') {
        const resolver = this.addressApprovalResolver;
        if (!resolver) return;
        this.addressApprovalResolver = null;
        resolver(choice);
    }

    private promptAddressApproval(original: AddressDetail | null, normalized: AddressDetail | null): Promise<'approve' | 'override' | 'cancel'> {
        this.addressApprovalOriginal = original;
        this.addressApprovalNormalized = normalized;
        this.showAddressApprovalModal = true;
        return new Promise(resolve => { this.addressApprovalResolver = resolve; });
    }

    private async validateAndConfirmAddress(patient: Patient): Promise<boolean> {
        if (!this.requireAddressValidationOnSave) return true;

        const addresses = (patient.addresses ?? []).filter(a => a?.address1?.trim());
        for (const address of addresses) {
            if (!(await this.validateSingleAddress(patient, address))) return false;
        }
        return true;
    }

    private async validateSingleAddress(patient: Patient, address: PatientAddress): Promise<boolean> {
        address.patientId = patient.id;
        let result: AddressValidationResult;
        try {
            result = (await axios.post<AddressValidationResult>('/Patient/ValidateAddress', address)).data;
        } catch {
            return true;
        }

        if (result.validationFailed) return true;

        if (result.addressChanged && result.normalizedAddress) {
            const choice = await this.promptAddressApproval(result.originalAddress, result.normalizedAddress);
            if (choice === 'cancel') return false;
            if (choice === 'approve') {
                const n = result.normalizedAddress;
                address.address1 = n.addressLine1 ?? undefined;
                address.address2 = n.addressLine2 ?? undefined;
                address.city = n.city ?? undefined;
                address.state = n.state ?? undefined;
                address.postalCode = n.postalCode ?? undefined;
            }
        }

        address.markAddressValidated = true;
        return true;
    }

    async submitUserInformation(eligibilityCallback: Function | null = null) {
        if (!this.patient) {
            console.warn("Don't have a patient.... we need to make a new one I guess!");
            this.createPatient();
        }

        const patient: Patient = this.patient as Patient;

        patient.firstName = this.displayName.firstName;
        patient.middleName = this.displayName.middleName;
        patient.lastName = this.displayName.lastName;

        const data = patient;
        data.programs = this.patientPrograms;

        this.isSaving = true;

        if (!(await this.validateAndConfirmAddress(patient))) {
            this.isSaving = false;
            return;
        }

        //Save patient
        try {
            const response = await axios.post<{ patient: Patient, warning: string }>('/Patient', data);

            if (eligibilityCallback && typeof (eligibilityCallback) == typeof (Function)) eligibilityCallback();
            const patient = response.data.patient;
            const warning = response.data.warning;

            //Save other meds, allergies, and diagnosis in parallel
            try {
                const savePromises: Promise<any>[] = [];

                //Commit other meds if new patient
                if (!this.patient.id) {
                    savePromises.push((this.otherMedsComponent as any).commitAllForPatient(patient.id));
                }

                if (this.allergiesModified) {
                    savePromises.push(axios.post(`/Allergy/SaveGSDDAllergies/${patient.id}`, {
                        allergies: this.patientAllergies,
                        removalReasons: [...(this.allergiesComponent?.removalReasons ?? [])],
                    }));
                }

                // Save diagnosis only if this was a new patient
                if (!this.patient.id && this.patientDiagnosis.length > 0) {
                    for (const diagnosis of this.patientDiagnosis) {
                        if (!diagnosis.icD10Code?.trim()) {
                            console.warn('Skipping diagnosis with empty icD10Code:', diagnosis);
                            continue;
                        }

                        savePromises.push(axios.post(`/Diagnosis/AddToPatient/${patient.id}`, {
                            icD10Code: diagnosis.icD10Code,
                            primaryPrograms: diagnosis.primaryPrograms || []
                        }));
                    }
                }

                await Promise.all(savePromises);
                this.allergiesModified = false;
                // Only discard the collected removal reasons once the reconcile has succeeded;
                // clearing earlier would wedge the user with no way to re-supply a reason on retry.
                if (this.allergiesComponent) this.allergiesComponent.removalReasons = [];

                this.newMode = false;
                this.patient = new Patient(patient);
                if (warning) {
                    this.$notification(NotificationOptions.warning(`Patient saved with warning: ${warning}`));
                } else {
                    this.$notification(NotificationOptions.successSaveNotificationPreset("Patient"));
                }

                if (!this.createNewPatient && !eligibilityCallback)
                    this.backToPatientDashboard();

                if (this.createNewPatient) {
                    this.$emit("created", this.patient);
                }
            } catch (err) {
                const error = err as AxiosError;
                const errorMsg = "Error while saving allergies and diagnosis";
                if (error.response?.status === 418) {
                    this.showLockedMessage(
                        EntityType.Patient,
                        patient?.id.toString(),
                        patient?.displayNamesForPerson(),
                        error.response.data
                    );
                } else {
                    this.$notification(NotificationOptions.error(error));
                }
                console.error(errorMsg, { error, response: error?.response });
            }
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Patient,
                    this.patient?.id.toString(),
                    this.patient?.displayNamesForPerson(),
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Patient", error));
            }
            console.error("Error while saving patient", err);
        } finally {
            this.isSaving = false;
        }
    }

    backToPatientDashboard() {
        if (this.createNewPatient) {
            this.$emit("close");
            return;
        }

        if (this.routePatientId) {
            this.$router.push({
                name: 'PatientDashboard',
                params: { id: this.routePatientId.toString() },
            });
        }
    }

    addProgram(newProgram: Program | null = null) {
        const selectedProgram: Program | null = newProgram || this.selectedProgram;
        if (!selectedProgram) return;

        const wasAdded = this.patientPrograms.filter(pp => pp.programID == selectedProgram.id)[0];
        if (wasAdded) return;

        const newPatientProgram = {
            programID: selectedProgram?.id,
            patientID: this.patient?.id,
            programName: selectedProgram?.name,
            identifier: '',
        } as PatientProgram;

        this.patientPrograms.push(newPatientProgram);
    }

    removeProgram(programID: number) {
        this.patientPrograms = this.patientPrograms.filter(p => p.programID != programID);
    }

    @Watch("patientAllergies")
    modPatientAllergies() {
        this.allergiesModified = true;
        if (this.patientAllergies.length) {
            this.patient.nkda = false;
        }
    }

    addNewAddress() {
        const negativeIdAddresses = this.patient.addresses.filter(addr => addr.id < 0);

        let newId: number;

        if (negativeIdAddresses.length) {
            const smallestNegativeId = Math.min(...negativeIdAddresses.map(addr => addr.id));
            newId = smallestNegativeId - 1;
        } else {
            newId = -1;
        }

        this.selectedAddress = {
            id: newId,
            type: PatientAddressType.mailing,
            isPrimary: false,
            patientId: this.patient.id
        };
    }

    editAddress(address: PatientAddress) {
        this.selectedAddress = structuredClone(toRaw(address));
    }

    processAddressFromModal() {
        if (!this.selectedAddress) return;

        if (this.selectedAddress.isPrimary) {
            this.patient.addresses.forEach(pa => {
                if (pa.id === this.selectedAddress?.id) return;
                pa.isPrimary = false;
            })
            this.patient.addresses = this.patient.addresses.filter(pa => pa.id !== this.selectedAddress?.id);
            this.patient.addresses.splice(0, 0, this.selectedAddress);
        } else {
            const existingIndex = this.patient.addresses.findIndex(pa => pa.id === this.selectedAddress!.id);

            if (existingIndex === -1) {
                this.patient.addresses.push(this.selectedAddress);
            } else {
                this.patient.addresses.splice(existingIndex, 1, this.selectedAddress);
            }
        }

        this.selectedAddress = null;
    }

    deleteAddress(address: PatientAddress) {
        this.patient.addresses = this.patient.addresses.filter(pa => pa.id !== address.id);
    }

    allowOnlyDigits(event: KeyboardEvent) {
        // Allow: backspace, delete, tab, escape, enter, arrows
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

        if (allowedKeys.includes(event.key)) {
            return; // Allow these keys
        }

        // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
        if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
            return; // Allow copy/paste/select
        }

        // Block if not a digit (0-9)
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }
}
</script>

<style scoped>
.addresses-container {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 350px), 1fr));
}
</style>
